import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/ui/Layout.tsx';
import { ProtectedRoute } from './components/ui/ProtectedRoute.tsx';
import { RequireMode } from './components/ui/RequireMode.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { startImpersonation } from './lib/auth.ts';
import { AdminOverviewPage } from './pages/AdminOverviewPage.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { CustomersPage } from './pages/CustomersPage.tsx';
import { CustomerDetailPage } from './pages/CustomerDetailPage.tsx';
import { SegmentsPage } from './pages/SegmentsPage.tsx';
import { SegmentDetailPage } from './pages/SegmentDetailPage.tsx';
import { AutomationsPage } from './pages/AutomationsPage.tsx';
import { AutomationDetailPage } from './pages/AutomationDetailPage.tsx';
import { CampaignsPage } from './pages/CampaignsPage.tsx';
import { CampaignDetailPage } from './pages/CampaignDetailPage.tsx';
import { SignalsPage } from './pages/SignalsPage.tsx';
import { SignalTypesPage } from './pages/SignalTypesPage.tsx';
import { TenantAnalyticsPage } from './pages/TenantAnalyticsPage.tsx';
import { TemplatesPage } from './pages/TemplatesPage.tsx';
import { TemplateDetailPage } from './pages/TemplateDetailPage.tsx';
import { TemplateBuilderPage } from './pages/TemplateBuilderPage.tsx';
import { TenantsPage } from './pages/TenantsPage.tsx';
import { TenantDetailPage } from './pages/TenantDetailPage.tsx';
import { LoyaltyProgramsPage } from './pages/LoyaltyProgramsPage.tsx';
import { LoyaltyDetailPage } from './pages/LoyaltyDetailPage.tsx';
import { ContestTestArenaPage } from './pages/ContestTestArenaPage.tsx';
import { IntegrationsPage } from './pages/IntegrationsPage.tsx';
import { TenantAuthSettingsTab } from './pages/TenantAuthSettingsTab.tsx';
import JourneyBuilderPage from './pages/JourneyBuilderPage.tsx';
import { PropertyDefinitionsPage } from './pages/PropertyDefinitionsPage.tsx';
// CMS V2
import { CmsSpacesPage } from './pages/CmsSpacesPage.tsx';
import { CmsSpaceDetailPage } from './pages/CmsSpaceDetailPage.tsx';
import { SpaceSettingsPage } from './pages/SpaceSettingsPage.tsx';
import { CmsPagesPage } from './pages/CmsPagesPage.tsx';
import { CmsPageEditorPage } from './pages/CmsPageEditorPage.tsx';
import { CmsCollectionsPage } from './pages/CmsCollectionsPage.tsx';
import { CmsEntriesPage } from './pages/CmsEntriesPage.tsx';
import { CmsEntryEditorPage } from './pages/CmsEntryEditorPage.tsx';
import { CmsNavPage } from './pages/CmsNavPage.tsx';
import { CmsNavMenusPage } from './pages/CmsNavMenusPage.tsx';
import { CmsLayoutsPage } from './pages/CmsLayoutsPage.tsx';
import { CmsLayoutDetailPage } from './pages/CmsLayoutDetailPage.tsx';
import { CmsRedirectsPage } from './pages/CmsRedirectsPage.tsx';
import { CmsSpaceLayout } from './components/cms/CmsSpaceLayout.tsx';
import { CmsHeadlessTokensPage } from './pages/CmsHeadlessTokensPage.tsx';
import { CmsBlockDefsPage } from './pages/CmsBlockDefsPage.tsx';
import { CmsCollectionTemplatesPage } from './pages/CmsCollectionTemplatesPage.tsx';
import { CmsCollectionTemplateEditorPage } from './pages/CmsCollectionTemplateEditorPage.tsx';
import { CmsVisualBuilderPage } from './pages/CmsVisualBuilderPage.tsx';
import { MediaLibraryPage } from './pages/MediaLibraryPage.tsx';
import { MediaDetailPage } from './pages/MediaDetailPage.tsx';
import { PlatformCostsPage } from './pages/PlatformCostsPage.tsx';
import { PlatformStatusPage } from './pages/PlatformStatusPage.tsx';
import { AnalyticsPage } from './pages/AnalyticsPage.tsx';
import { ShortlinksPage } from './pages/ShortlinksPage.tsx';
import { TeamPage } from './pages/TeamPage.tsx';
// Questionnaires
import { QuestionnairesPage } from './pages/QuestionnairesPage.tsx';
import { QuestionnaireDetailPage } from './pages/QuestionnaireDetailPage.tsx';
// Event Management
import { EventsListPage } from './pages/EventsListPage.tsx';
import { EventFormPage } from './pages/EventFormPage.tsx';
import { EventDetailPage } from './pages/EventDetailPage.tsx';
import { ToursListPage } from './pages/ToursListPage.tsx';
import { TourFormPage } from './pages/TourFormPage.tsx';
import { TourDetailPage } from './pages/TourDetailPage.tsx';

// ── Impersonation bootstrap (module-level, runs before first render) ──────────
// When a super-admin is redirected to a tenant subdomain with ?token=<jwt>,
// we must activate the impersonation token synchronously here — before any
// ProtectedRoute or RequireMode guard runs — otherwise they see no token and
// redirect to /login before the token is stored.
(function bootstrapImpersonationToken() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) {
    startImpersonation(token);
    params.delete('token');
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);
  }
})();

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />

      {/* ── Full-screen routes (no Layout sidebar) ── */}
      <Route
        path="/templates/:id/edit"
        element={
          <ProtectedRoute>
            <RequireMode mode="tenant">
              <TemplateBuilderPage />
            </RequireMode>
          </ProtectedRoute>
        }
      />
      <Route
        path="/automations/:id/build"
        element={
          <ProtectedRoute>
            <RequireMode mode="tenant">
              <JourneyBuilderPage />
            </RequireMode>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spaces/:spaceId/pages/:pageId/visual-edit"
        element={
          <ProtectedRoute>
            <RequireMode mode="tenant">
              <CmsVisualBuilderPage />
            </RequireMode>
          </ProtectedRoute>
        }
      />

      {/* Protected — inside Layout shell */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* ── Admin mode routes (super_admin, not impersonating) ── */}
        <Route
          path="admin"
          element={
            <RequireMode mode="admin">
              <AdminOverviewPage />
            </RequireMode>
          }
        />
        <Route
          path="admin/costs"
          element={
            <RequireMode mode="admin">
              <PlatformCostsPage />
            </RequireMode>
          }
        />
        <Route
          path="admin/status"
          element={
            <RequireMode mode="admin">
              <PlatformStatusPage />
            </RequireMode>
          }
        />
        <Route
          path="admin/tenants"
          element={
            <RequireMode mode="admin">
              <TenantsPage />
            </RequireMode>
          }
        />
        <Route
          path="admin/tenants/:id"
          element={
            <RequireMode mode="admin">
              <TenantDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="admin/analytics"
          element={
            <RequireMode mode="admin">
              <AnalyticsPage />
            </RequireMode>
          }
        />

        {/* ── Tenant mode routes (regular users OR impersonating super_admin) ── */}
        <Route
          index
          element={
            <RequireMode mode="tenant">
              <DashboardPage />
            </RequireMode>
          }
        />
        <Route
          path="customers"
          element={
            <RequireMode mode="tenant">
              <CustomersPage />
            </RequireMode>
          }
        />
        <Route
          path="customers/:id"
          element={
            <RequireMode mode="tenant">
              <CustomerDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="segments"
          element={
            <RequireMode mode="tenant">
              <SegmentsPage />
            </RequireMode>
          }
        />
        <Route
          path="segments/:id"
          element={
            <RequireMode mode="tenant">
              <SegmentDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="automations"
          element={
            <RequireMode mode="tenant">
              <AutomationsPage />
            </RequireMode>
          }
        />
        <Route
          path="automations/:id"
          element={
            <RequireMode mode="tenant">
              <AutomationDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="campaigns"
          element={
            <RequireMode mode="tenant">
              <CampaignsPage />
            </RequireMode>
          }
        />
        <Route
          path="campaigns/:id"
          element={
            <RequireMode mode="tenant">
              <CampaignDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="signals"
          element={
            <RequireMode mode="tenant">
              <SignalsPage />
            </RequireMode>
          }
        />
        <Route
          path="signal-types"
          element={
            <RequireMode mode="tenant">
              <SignalTypesPage />
            </RequireMode>
          }
        />
        <Route
          path="analytics"
          element={
            <RequireMode mode="tenant">
              <TenantAnalyticsPage />
            </RequireMode>
          }
        />
        <Route
          path="templates"
          element={
            <RequireMode mode="tenant">
              <TemplatesPage />
            </RequireMode>
          }
        />
        <Route
          path="templates/:id"
          element={
            <RequireMode mode="tenant">
              <TemplateDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="loyalty"
          element={
            <RequireMode mode="tenant">
              <LoyaltyProgramsPage />
            </RequireMode>
          }
        />
        <Route
          path="loyalty/:id"
          element={<RequireMode mode="tenant"><Navigate to="overview" replace /></RequireMode>}
        />
        <Route
          path="loyalty/:id/:section"
          element={
            <RequireMode mode="tenant">
              <LoyaltyDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="loyalty/:id/contests/:contestId/test-arena"
          element={
            <RequireMode mode="tenant">
              <ContestTestArenaPage />
            </RequireMode>
          }
        />
        <Route
          path="integrations"
          element={
            <RequireMode mode="tenant">
              <IntegrationsPage />
            </RequireMode>
          }
        />
        <Route
          path="integrations/auth"
          element={
            <RequireMode mode="tenant">
              <TenantAuthSettingsTab />
            </RequireMode>
          }
        />
        <Route
          path="settings/properties"
          element={
            <RequireMode mode="tenant">
              <PropertyDefinitionsPage />
            </RequireMode>
          }
        />
        {/* ── CMS V2 routes ── */}
        <Route
          path="spaces"
          element={
            <RequireMode mode="tenant">
              <CmsSpacesPage />
            </RequireMode>
          }
        />
        {/* ── CMS Space — sidebar layout for list/hub pages ── */}
        <Route
          path="spaces/:spaceId"
          element={
            <RequireMode mode="tenant">
              <CmsSpaceLayout />
            </RequireMode>
          }
        >
          <Route index element={<CmsSpaceDetailPage />} />
          <Route path="pages" element={<CmsPagesPage />} />
          <Route path="collections" element={<CmsCollectionsPage />} />
          <Route path="collections/:colId/entries" element={<CmsEntriesPage />} />
          <Route path="menus" element={<CmsNavMenusPage />} />
          <Route path="layouts" element={<CmsLayoutsPage />} />
          <Route path="collection-templates" element={<CmsCollectionTemplatesPage />} />
          <Route path="redirects" element={<CmsRedirectsPage />} />
          <Route path="settings" element={<SpaceSettingsPage />} />
        </Route>
        {/* ── CMS Space — full-screen editors (no sidebar) ── */}
        <Route
          path="spaces/:spaceId/pages/:pageId"
          element={
            <RequireMode mode="tenant">
              <CmsPageEditorPage />
            </RequireMode>
          }
        />
        <Route
          path="spaces/:spaceId/collections/:colId/entries/:entryId"
          element={
            <RequireMode mode="tenant">
              <CmsEntryEditorPage />
            </RequireMode>
          }
        />
        <Route
          path="spaces/:spaceId/nav"
          element={
            <RequireMode mode="tenant">
              <CmsNavPage />
            </RequireMode>
          }
        />
        <Route
          path="spaces/:spaceId/menus/:menuId/items"
          element={
            <RequireMode mode="tenant">
              <CmsNavPage />
            </RequireMode>
          }
        />
        <Route
          path="spaces/:spaceId/layouts/:layoutId"
          element={
            <RequireMode mode="tenant">
              <CmsLayoutDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="spaces/:spaceId/collection-templates/:templateId"
          element={
            <RequireMode mode="tenant">
              <CmsCollectionTemplateEditorPage />
            </RequireMode>
          }
        />
        <Route
          path="cms/tokens"
          element={
            <RequireMode mode="tenant">
              <CmsHeadlessTokensPage />
            </RequireMode>
          }
        />
        <Route
          path="cms/block-defs"
          element={
            <RequireMode mode="tenant">
              <CmsBlockDefsPage />
            </RequireMode>
          }
        />
        <Route
          path="media"
          element={
            <RequireMode mode="tenant">
              <MediaLibraryPage />
            </RequireMode>
          }
        />
        <Route
          path="media/:id"
          element={
            <RequireMode mode="tenant">
              <MediaDetailPage />
            </RequireMode>
          }
        />
        <Route
          path="shortlinks"
          element={
            <RequireMode mode="tenant">
              <ShortlinksPage />
            </RequireMode>
          }
        />
        <Route
          path="team"
          element={
            <RequireMode mode="tenant">
              <TeamPage />
            </RequireMode>
          }
        />
        {/* ── Questionnaires routes ── */}
        <Route
          path="questionnaires"
          element={
            <RequireMode mode="tenant">
              <QuestionnairesPage />
            </RequireMode>
          }
        />
        <Route
          path="questionnaires/:id"
          element={
            <RequireMode mode="tenant">
              <QuestionnaireDetailPage />
            </RequireMode>
          }
        />
        {/* ── Event Management routes ── */}
        <Route
          path="events"
          element={
            <RequireMode mode="tenant">
              <EventsListPage />
            </RequireMode>
          }
        />
        <Route
          path="events/new"
          element={
            <RequireMode mode="tenant">
              <EventFormPage />
            </RequireMode>
          }
        />
        <Route
          path="events/:id"
          element={
            <RequireMode mode="tenant">
              <EventDetailPage />
            </RequireMode>
          }
        />
        {/* ── Tours routes ── */}
        <Route
          path="events/tours"
          element={
            <RequireMode mode="tenant">
              <ToursListPage />
            </RequireMode>
          }
        />
        <Route
          path="events/tours/new"
          element={
            <RequireMode mode="tenant">
              <TourFormPage />
            </RequireMode>
          }
        />
        <Route
          path="events/tours/:id"
          element={
            <RequireMode mode="tenant">
              <TourDetailPage />
            </RequireMode>
          }
        />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
